import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractTemplatesApi } from '../api/contractTemplatesApi';
import type {
  CreateContractTemplatePayload,
  CreateContractTemplateVersionPayload,
  SetContractTemplateFieldsPayload,
} from '../types';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const KEY = 'contract-templates';

export const useContractTemplates = () =>
  useQuery({
    queryKey: [KEY],
    queryFn: () => contractTemplatesApi.list(),
    staleTime: 60 * 1000,
  });

/** A template version's Required/Optional fields. Disabled until an id is chosen. */
export const useContractTemplateFields = (id: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, id, 'fields'],
    queryFn: () => contractTemplatesApi.getFields(id as string),
    enabled: !!id,
    staleTime: 60 * 1000,
  });

export const useCreateContractTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateContractTemplatePayload) => contractTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Contract template created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create contract template')),
  });
};

export const useCreateContractTemplateVersion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: CreateContractTemplateVersionPayload }) =>
      contractTemplatesApi.createVersion(name, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('New template version created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create template version')),
  });
};

export const useActivateContractTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => contractTemplatesApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Template version activated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to activate template')),
  });
};

export const useSetContractTemplateFields = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SetContractTemplateFieldsPayload }) =>
      contractTemplatesApi.setFields(id, data),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: [KEY, id, 'fields'] });
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Field configuration saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save field configuration')),
  });
};
